import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

// Components
import { FormSettingsProject } from 'Organisms/Forms'
import LoadingButton from 'Organisms/LoadingButton'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import { Loading } from 'Organisms/Loading'
import BudgetSettings from './BudgetSettings'
import BillingSettings from './BillingSettings'

// Hooks
import { useClient, useModal, useProject } from 'Hooks'

// Utils
import { FIELD_CLIENT_ID_PROJECT } from 'Pages/Projects/ProjectModal'
import { PROJECT_CODE_ALREADY_EXISTS } from 'Utils/const/messages'
import {
  buildBudgetPayload,
  buildSettingsDefaults,
  billingHasChanged,
  budgetHasChanged,
  summarizeChanges
} from './settingsHelpers'

const FORM_ID = 'projectSettingsForm'

const Settings = ({ project, refreshProjectInfo }) => {
  const [showDropdown, setShowDropdown] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [result, setResult] = useState({ open: false })

  // Hooks
  const { loadingProjectHook, updateProjectHook, saveBudgetToProjectHook, saveBillingToProjectHook } = useProject()
  const { listClients, loadingClientHook, getClientsHook, createClientHook } = useClient()

  const LOADING = loadingClientHook || loadingProjectHook || saving

  const methods = useForm({
    shouldUnregister: true,
    defaultValues: buildSettingsDefaults(project)
  })
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setError,
    formState: { errors }
  } = methods

  useEffect(() => {
    getClientsHook()
  }, [])

  useEffect(() => {
    reset(buildSettingsDefaults(project))
  }, [project, reset])

  const handleChangeDropdownClient = () => {
    methods.setValue(FIELD_CLIENT_ID_PROJECT, showDropdown ? '' : project.client.id)
    setShowDropdown((prev) => !prev)
  }

  // Step 1: validate then ask for confirmation
  const onValid = () => setConfirmVisible(true)

  // Step 2: persist everything from a single place
  const persistChanges = async () => {
    const values = getValues()
    setConfirmVisible(false)
    setSaving(true)

    try {
      let clientId = values[FIELD_CLIENT_ID_PROJECT]

      if (!showDropdown) {
        const resClient = await createClientHook({ nameClient: clientId })
        if (!resClient.success) {
          setError('client_id', { type: 'exist', message: resClient.message })
          return
        }
        clientId = resClient.data.id
      }

      const problems = []

      const resProject = await updateProjectHook({
        projectId: project.id,
        body: {
          [FIELD_CLIENT_ID_PROJECT]: clientId,
          name: values.name,
          code: values.code,
          note: values.note
        }
      })
      if (resProject?.error) {
        if ((resProject.message || '').includes(PROJECT_CODE_ALREADY_EXISTS)) {
          setError('code', { type: 'custom', message: PROJECT_CODE_ALREADY_EXISTS })
        } else {
          problems.push(resProject.message || 'The project details could not be saved.')
        }
      }

      if (budgetHasChanged(values, project)) {
        const resBudget = await saveBudgetToProjectHook({ projectId: project.id, body: buildBudgetPayload(values) })
        if (!resBudget.success) problems.push(resBudget.message)
      }

      if (billingHasChanged(values, project)) {
        const resBilling = await saveBillingToProjectHook({
          projectId: project.id,
          body: { method: values.billing_method }
        })
        if (!resBilling.success) problems.push(resBilling.message)
      }

      if (problems.length) {
        setResult({ open: true, error: true, message: problems.join(' ') })
        return
      }

      await Promise.allSettled([getClientsHook(), refreshProjectInfo()])
      setResult({ open: true, error: false, message: 'The changes have been saved successfully' })
    } catch (e) {
      setResult({ open: true, error: true, message: e?.message || 'Something went wrong while saving.' })
    } finally {
      setSaving(false)
    }
  }

  const changeRows = confirmVisible ? summarizeChanges(getValues(), project) : []

  return (
    <>
      {LOADING && <Loading />}

      <FormProvider {...methods}>
        <form id={FORM_ID} className="row justify-content-center" onSubmit={handleSubmit(onValid)}>
          <div className="col-lg-11 col-12 d-grid">
            <h3 className="mb-3">Project Settings</h3>

            <FormSettingsProject
              renderAsForm={false}
              register={register}
              clients={listClients}
              errors={errors}
              showDropdown={showDropdown}
              onChangeShowDropdown={handleChangeDropdownClient}
              defaultClient={project.client.id}
            />
          </div>

          <BudgetSettings />
          <BillingSettings />

          <div className="col-lg-11 col-12">
            <div className="row justify-content-end">
              <div className="col-auto my-4">
                <LoadingButton type="submit" form={FORM_ID} className="btn btn-primary" disabled={saving}>
                  Save changes
                </LoadingButton>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>

      <SaveConfirmationModal
        visible={confirmVisible}
        setVisible={setConfirmVisible}
        rows={changeRows}
        onConfirm={persistChanges}
      />

      {result.open && (
        <ModalSuccess
          visible
          setVisible={() => setResult({ open: false })}
          error={result.error}
          title={result.message}
          width={380}
          btnText="Close"
        />
      )}
    </>
  )
}

const SaveConfirmationModal = ({ visible, setVisible, rows, onConfirm }) => {
  const { Modal, hide } = useModal({ identifier: 'projectSettingsConfirm', visible, setVisible })

  return (
    <Modal>
      <div className="modal-content" style={{ maxWidth: 520 }}>
        <div className="modal-header align-items-start border-0">
          <h2 className="h4 mb-0">Confirm settings update</h2>
          <button type="button" className="btn-close" onClick={hide} aria-label="Close" />
        </div>
        <div className="modal-body">
          <p>Do you want to save the following changes to this project?</p>
          <div className="card card-secondary py-3 px-4">
            {rows.map(({ label, value }) => (
              <p className="mb-1" key={label}>
                {label}: <span className="text-active">{String(value || '—')}</span>
              </p>
            ))}
          </div>
          <div className="row mt-4 justify-content-end">
            <div className="col-auto">
              <button type="button" className="btn btn-secondary" onClick={hide}>
                Cancel
              </button>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-primary" onClick={onConfirm}>
                Save changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default Settings
